import React, { useEffect, useState, useMemo, useCallback } from "react";
import { StyledH2 } from "@aureskonnect/react-ui";
import { t } from "i18next";
import Switch from "react-switch";
import { AvGroup, AvForm } from "availity-reactstrap-validation";
import CreatableSelect from "react-select/creatable";
import { Button } from "reactstrap";
import classnames from "classnames";
import { useSnapshot } from "valtio";
import { addHours } from "date-fns";
import moment from "moment";

import ErrorToast from "@components/Common/ErrorTost";
import {
    setDuration,
    setFuseauHoraire,
    setIsModalOpenedOrders,
    setPattern,
    setOrders,
    store,
} from "../../store";
import {
    offsetToHours,
    timezone_data,
    translatePattern,
    useSuspensionMessage,
    useTimeCalculations,
} from "./OrdersHooks";
import ModalOrders from "../ModalOrders";
import { ConfirmationPauseOrdersMessage } from "../ConfirmationPauseOrdersMessage";
import { ConfirmationTimeZone } from "../ConfirmationTimeZone";

function OrdersInterrupt({ selectedRows }: { selectedRows: any[] }) {
    const {
        fuseau_horaire,
        isModalOpenedOrders,
        duration,
        pattern,
    } = useSnapshot(store);

    const getInitialFuseauHoraire = useCallback(() => {
        return selectedRows[0]?.fuseau_horraire || "0100";
    }, [selectedRows]);

    const [selectedValue, setSelectedValue] = useState(
        getInitialFuseauHoraire()
    );
    const [isComingFromModify, setIsComingFromModify] = useState(false);
    const [isModalConfirmationOpened, setIsModalConfirmationOpened] = useState(
        false
    );
    const [isValidation, setIsValidation] = useState(false);
    const [value, setValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    const [isPauseOrders, setIsPauseOrders] = useState<boolean>(
        selectedRows[0]?.["Pause orders"] || false
    );

    const [localDuration, setLocalDuration] = useState<any>(duration || "");
    const [localPattern, setLocalPattern] = useState<any>(pattern || "");
    const [didInit, setDidInit] = useState(false);

    const timeCalculations = useTimeCalculations(
        selectedRows,
        fuseau_horaire,
        localDuration
    );

    const timezoneOptions = useMemo(
        () =>
            Object.entries(timezone_data).map(
                ([label, { value, countries }]) => ({
                    value,
                    label: `${label} – ${countries
                        .map((c) => t(c))
                        .join(", ")}`,
                })
            ),
        []
    );

    const message = useSuspensionMessage(
        isPauseOrders,
        fuseau_horaire,
        localDuration,
        localPattern,
        timeCalculations.projectedEndTime
    );

    const handleTimezoneChange = useCallback((newValue: string) => {
        setSelectedValue(newValue);
        setFuseauHoraire(newValue);
        setIsPauseOrders(false);
        setLocalDuration("");
        setDuration("");
        setLocalPattern("");
        setPattern("");
        setOrders({
            localDuration: "",
            localPattern: "",
            isPauseOrders: false,
            interrompreIndeterminé: false,
            interompreJusqua: "",
        });
    }, []);

    const handleSwitchToggle = useCallback(() => {
        if (fuseau_horaire === "") {
            ErrorToast(
                t(
                    "Attention, to configure command interruption, please choose a time zone."
                )
            );
            return;
        }

        setIsComingFromModify(false);
        if (isPauseOrders) {
            setIsModalConfirmationOpened(true);
        } else {
            setIsPauseOrders(true);
            setIsModalOpenedOrders(true);
        }
    }, [fuseau_horaire, isPauseOrders]);

    const handleEditClick = useCallback(() => {
        setIsModalOpenedOrders(true);
        setIsComingFromModify(true);
    }, []);

    useEffect(() => {
        const interrompreIndeterminé =
            localDuration.label === t("Undetermined");

        const interompreJusqua = moment(
            timeCalculations.projectedEndTime
        ).format("YYYY-MM-DD HH:mm:ss");

        setOrders({
            localDuration,
            localPattern,
            isPauseOrders,
            interrompreIndeterminé,
            interompreJusqua,
        });

        if (localDuration !== duration) {
            setDuration(localDuration);
        }
        if (localPattern !== pattern) {
            setPattern(localPattern);
        }
        // eslint-disable-next-line
    }, [
        localDuration,
        localPattern,
        isPauseOrders,
        timeCalculations.projectedEndTime,
    ]);

    useEffect(() => {
        const initialTimezone = getInitialFuseauHoraire();
        if (initialTimezone !== fuseau_horaire) {
            setFuseauHoraire(initialTimezone);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (didInit || !selectedRows?.length) return;

        const row = selectedRows[0];
        const timezone = row?.fuseau_horraire || "0100";
        const now = addHours(new Date(), offsetToHours(timezone));
        const targetDate = row.date ? new Date(row.date) : null;

        let initPause = false;
        let initDuration: any = "";
        let initPattern: any = "";

        if (row["Pause orders"]) {
            initPause = true;

            if (row.Undetermined) {
                initDuration = {
                    label: t("Undetermined"),
                    value: t("Undetermined"),
                };
                const pat = translatePattern(row.motif_interompre);
                initPattern = { label: pat, value: pat };
            } else if (targetDate && now < targetDate) {
                const diffMin = Math.floor(
                    (targetDate.getTime() - now.getTime()) / (1000 * 60)
                );
                initDuration = { label: diffMin, value: diffMin };
                const pat = translatePattern(row.motif_interompre);
                initPattern = { label: pat, value: pat };
            } else {
                initPause = false;
            }
        }

        setSelectedValue(timezone);
        setFuseauHoraire(timezone);
        setIsPauseOrders(initPause);
        setLocalDuration(initDuration);
        setLocalPattern(initPattern);

        setDuration(initDuration);
        setPattern(initPattern);
        setOrders({
            localDuration: initDuration,
            localPattern: initPattern,
            isPauseOrders: initPause,
            interrompreIndeterminé: initDuration.label === t("Undetermined"),
            interompreJusqua: row.date || "",
        });

        setDidInit(true);
    }, [selectedRows, didInit]);

    return (
        <div>
            <div className=" d-flex justify-content-between align-items-center">
                <StyledH2
                    style={{
                        font: "normal normal 400 20px/27px Segoe UI",
                        color: "#000000",
                        opacity: "1",
                    }}
                    className="ml-5 pt-3"
                >
                    {t("Pause orders")}
                </StyledH2>
                <Switch
                    className="cac_icn_interruptCommands mr-4"
                    offColor="#faafb4"
                    offHandleColor="#E30613"
                    onColor="#c2eec4"
                    onHandleColor="#34C38F"
                    height={19}
                    handleDiameter={24}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    checked={isPauseOrders}
                    onChange={handleSwitchToggle}
                />
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3 ml-3 px-5">
                <span
                    className="ml-5"
                    style={{
                        font: "normal normal 400 18px/24px Segoe UI",
                        color: "#000000",
                        opacity: "1",
                    }}
                >
                    {message}
                </span>
                <Button
                    className={classnames("style-button-order__clz", {
                        "not-allowed-icon__clz": !isPauseOrders,
                    })}
                    onClick={handleEditClick}
                    disabled={!isPauseOrders}
                >
                    <span
                        className="cac_btn_editInterruptcommands"
                        style={{
                            font: "normal normal 400 18px/24px Segoe UI",
                            color: "rgb(0, 112, 255)",
                            textDecoration: "underline",
                        }}
                    >
                        {t("Edit")}
                    </span>
                </Button>
            </div>

            <AvForm>
                <AvGroup className=" d-flex justify-content-align mt-3 ml-3 px-5">
                    <span
                        className=" ml-5"
                        style={{
                            font: "normal normal 400 16px/24px Segoe UI",
                            color: "#000000",
                            opacity: "1",
                            width: "329px",
                        }}
                    >
                        {t("Shop time zone")}
                    </span>
                    <CreatableSelect
                        isClearable
                        value={
                            timezoneOptions.find(
                                (opt) => opt.value === selectedValue
                            ) || null
                        }
                        className="mb-2 mr-2"
                        options={timezoneOptions}
                        onChange={(e: any) => {
                            const newValue = e?.value || "";
                            if (
                                message !==
                                t("No interrupt commands are enabled")
                            ) {
                                setIsValidation(true);
                                setValue(newValue);
                            } else {
                                handleTimezoneChange(newValue);
                            }
                        }}
                        placeholder={t("Select timezone")}
                        styles={{
                            container: (base) => ({ ...base, width: "400px" }),
                            control: (base) => ({
                                ...base,
                                borderRadius: "6px",
                                minHeight: "40px",
                                borderColor: isFocused ? "#83bceb" : "#ccc",
                                boxShadow: isFocused
                                    ? "0 0 4px 4px rgba(131, 192, 243, 0.6)"
                                    : "none",
                            }),
                            menu: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                            menuList: (base) => ({
                                ...base,
                                maxHeight: "180px",
                                overflowY: "auto",
                            }),
                        }}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                </AvGroup>
            </AvForm>

            {isModalOpenedOrders && (
                <ModalOrders
                    setLocalDuration={setLocalDuration}
                    localDuration={localDuration}
                    setLocalPattern={setLocalPattern}
                    localPattern={localPattern}
                    isPauseOrders={isPauseOrders}
                    selectedRows={selectedRows}
                    setIsPauseOrders={setIsPauseOrders}
                    isComingFromModify={isComingFromModify}
                    setIsComingFromModify={setIsComingFromModify}
                />
            )}

            {isModalConfirmationOpened && (
                <ConfirmationPauseOrdersMessage
                    setLocalPattern={setLocalPattern}
                    setLocalDuration={setLocalDuration}
                    setIsModalConfirmationOpened={setIsModalConfirmationOpened}
                    isModalConfirmationOpened={isModalConfirmationOpened}
                    setIsPauseOrders={setIsPauseOrders}
                    isPauseOrders={isPauseOrders}
                />
            )}

            {isValidation && (
                <ConfirmationTimeZone
                    setSelectedValue={setSelectedValue}
                    isModalOpened={isValidation}
                    setIsModalOpened={setIsValidation}
                    value={value}
                    handleTimezoneChange={handleTimezoneChange}
                />
            )}
        </div>
    );
}

export default OrdersInterrupt;
